import React, { useState } from "react";
import { useGlobalData } from "@/lib/dataStreams";
import { Database, Lightning, Target, CheckCircle, WarningCircle, CaretDown } from "@phosphor-icons/react";

const FaqItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-slate-800 group bg-slate-900/20 hover:bg-slate-900/40 transition-colors px-6 rounded-2xl mb-4">
      <button 
        onClick={() => setOpen(!open)} 
        className="w-full py-6 flex justify-between items-center text-left"
      >
        <span className="font-bold text-lg text-slate-200 group-hover:text-teal-400 transition-colors pr-4">{q}</span>
        <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-colors ${open ? 'bg-teal-500/20 text-teal-400' : 'bg-slate-800 text-slate-400'}`}>
          <CaretDown size={16} className={`transform transition-transform duration-300 ${open ? 'rotate-180' : ''}`} />
        </div>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${open ? 'max-h-96 opacity-100 pb-6' : 'max-h-0 opacity-0'}`}>
        <p className="text-slate-300 leading-relaxed text-sm sm:text-base pr-8">
          {a}
        </p>
      </div>
    </div>
  );
};

const fmtUsd = (v) => {
  if (!(v > 0)) return "—";
  if (v >= 1000000) return `$${(v / 1000000).toFixed(2)}M`;
  if (v >= 1000) return `$${(v / 1000).toFixed(1)}k`;
  return `$${v.toFixed(0)}`;
};

function MigrationRadar({ radarTokens }) {
  return (
    <div className="mb-16">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-sm uppercase tracking-wider text-slate-400 flex items-center gap-2">
          <Target size={18} className="text-teal-400" /> Migration Radar
        </h3>
        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider bg-slate-900 px-3 py-1 rounded-full border border-slate-800">
          Top Graduation Candidates
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {radarTokens?.length > 0 ? (
          radarTokens.map((t) => (
            <div key={t.mint} className="bg-slate-900 border border-slate-800 hover:border-teal-500/50 transition-colors rounded-2xl p-5 shadow-sm relative overflow-hidden">
              <div className="absolute top-0 right-0 w-24 h-24 bg-teal-500/5 rounded-full blur-xl pointer-events-none" />
              <div className="flex items-center gap-4 mb-5 relative z-10">
                <img src={t.image} alt="token" className="w-12 h-12 rounded-xl border border-slate-700 bg-slate-950 p-1" />
                <div className="overflow-hidden">
                  <div className="font-bold text-base text-slate-100 truncate">{t.name} <span className="text-slate-500 font-medium text-sm">({t.ticker})</span></div>
                  <div className="text-[10px] text-slate-400 font-mono truncate bg-slate-950 px-2 py-0.5 rounded mt-1 inline-block border border-slate-800">{t.mint.substring(0,8)}...{t.mint.substring(36)}</div>
                </div>
              </div>
              <div className="space-y-2 relative z-10">
                <div className="flex justify-between text-[10px] font-bold uppercase tracking-wider font-sans text-slate-500">
                  <span>Est. Curve Progress</span>
                  <span className="text-teal-400">{t.progress.toFixed(2)}%</span>
                </div>
                <div className="h-2 w-full bg-slate-950 rounded-full overflow-hidden border border-slate-800">
                  <div className="h-full bg-gradient-to-r from-emerald-500 to-teal-400 transition-all duration-500" style={{ width: `${t.progress}%` }} />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-4 pt-4 border-t border-slate-800/60 relative z-10 text-center">
                <div>
                  <div className="text-[9px] uppercase tracking-wider text-slate-500 font-bold font-sans">Liquidity</div>
                  <div className="text-xs font-mono text-slate-200 mt-1">{fmtUsd(t.liquidity)}</div>
                </div>
                <div>
                  <div className="text-[9px] uppercase tracking-wider text-slate-500 font-bold font-sans">Holders</div>
                  <div className="text-xs font-mono text-slate-200 mt-1">{t.holders > 0 ? t.holders.toLocaleString() : "—"}</div>
                </div>
                <div>
                  <div className="text-[9px] uppercase tracking-wider text-slate-500 font-bold font-sans">MCap</div>
                  <div className="text-xs font-mono text-slate-200 mt-1">{fmtUsd(t.mcap)}</div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="col-span-4 p-10 text-center text-slate-500 text-sm animate-pulse bg-slate-900 rounded-2xl border border-dashed border-slate-700">
            Scanning for tokens approaching migration...
          </div>
        )}
      </div>
    </div>
  );
}

function ExecutionLog({ trades }) {
  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-sm uppercase tracking-wider text-slate-400">Bot Execution Log</h3>
        <div className="flex items-center gap-2 bg-teal-500/10 px-3 py-1.5 rounded-full border border-teal-500/20">
          <span className="w-2 h-2 bg-teal-400 rounded-full animate-pulse shadow-[0_0_8px_rgba(45,212,191,0.8)]" />
          <span className="text-teal-400 text-[10px] font-bold tracking-wider uppercase">Streaming Trades</span>
        </div>
      </div>
      
      <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm font-sans text-left whitespace-nowrap">
            <thead>
              <tr className="border-b border-slate-800 bg-slate-950/50 text-slate-400 text-xs uppercase tracking-wider font-bold">
                <th className="p-5">Time (UTC)</th>
                <th className="p-5">Target Token</th>
                <th className="p-5">Action</th>
                <th className="p-5">Value (USD)</th>
                <th className="p-5">Realized PnL</th>
                <th className="p-5">Snipe Status</th>
                <th className="p-5 text-right">Mainnet Signature</th>
              </tr>
            </thead>
            <tbody className="font-mono text-sm">
              {trades.length === 0 ? (
                <tr><td colSpan="7" className="p-8 text-center text-slate-500 text-xs animate-pulse font-sans">Scanning pump.fun mainnet stream...</td></tr>
              ) : (
                trades.map((t) => {
                  const inProgress = t.status === "IN_PROGRESS";
                  const success = t.status === "SUCCESS";
                  return (
                    <tr key={t.signature} className="border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors">
                      <td className="p-5 text-slate-400 text-[11px] tracking-wider">
                        {t.ts ? new Date(t.ts).toLocaleTimeString([], { hour12: false, timeZone: "UTC" }) : "—"}
                      </td>
                      <td className="p-5">
                        <div className="font-bold text-slate-200 font-sans">{t.name} <span className="text-slate-500 font-medium">({t.ticker})</span></div>
                        <div className="text-[11px] text-slate-500 truncate max-w-[150px] mt-1">{t.mint}</div>
                      </td>
                      <td className="p-5">
                        <span className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-md font-sans ${t.isBuy === false ? "bg-rose-500/10 text-rose-400" : "bg-indigo-500/10 text-indigo-400"}`}>
                          {t.isBuy === false ? "Snipe Sell" : "Snipe Buy"}
                        </span>
                      </td>
                      <td className="p-5 text-slate-200 font-bold">
                        {t.usd > 0 ? `$${t.usd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—"}
                      </td>
                      <td className="p-5 font-bold">
                        {t.isBuy !== false ? (
                          <span className="text-slate-600">—</span>
                        ) : t.pnl === null || t.pnl === undefined ? (
                          <span className="text-slate-500 animate-pulse">…</span>
                        ) : (
                          <span className={t.pnl >= 0 ? "text-emerald-400" : "text-rose-400"}>
                            {t.pnl >= 0 ? "+" : "−"}${Math.abs(t.pnl).toFixed(2)}
                          </span>
                        )}
                      </td>
                      <td className="p-5 font-sans font-bold">
                        {inProgress ? (
                          <div className="flex items-center gap-1.5 text-xs text-amber-400 animate-pulse">
                            <span className="w-2 h-2 rounded-full bg-amber-400" /> IN PROGRESS
                          </div>
                        ) : (
                          <div className={`flex items-center gap-1.5 text-xs ${success ? 'text-emerald-400' : 'text-rose-400'}`}>
                            {success ? <CheckCircle size={16} weight="fill" /> : <WarningCircle size={16} weight="fill" />}
                            {t.status}
                          </div>
                        )}
                      </td>
                      <td className="p-5 text-right text-slate-500 text-xs truncate max-w-[150px]">
                        <a href={`https://solscan.io/tx/${t.signature}`} target="_blank" rel="noreferrer" className="hover:text-teal-400 transition-colors underline decoration-slate-700 underline-offset-4">
                          {t.signature}
                        </a>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

export default function PumpMigrationBot() {
  const { pumpTrades, radarTokens } = useGlobalData();
  const trades = pumpTrades.slice(0, 15);

  const faqs = [
    {
      q: "How exactly do you predict Pump.fun graduation?",
      a: "Pump.fun relies on a completely deterministic bonding curve. A token graduates to a Raydium pool exactly when its curve hits 100%, which corresponds to roughly 85 SOL (or ~$69,000 USD) deposited into the smart contract. We continuously pull real-time WebSocket data to map the micro-lamport distance to this cap, calculating exactly when the migration threshold is breached."
    },
    {
      q: "Why snipe on Raydium instead of buying directly on Pump.fun?",
      a: "Buying on Pump.fun before graduation carries high risk (the token may never graduate). By sniping 'Block-0' on Raydium, we execute our buy at the exact moment the token is seeded with new, massive liquidity. This allows us to instantly ride the retail volume surge that occurs when the token hits major DEX screeners like DexScreener or Birdeye."
    },
    {
      q: "What causes a 'Snipe Missed' status?",
      a: "A snipe can miss due to heavy network congestion or being outbid on priority fees by competing MEV bots. If a transaction fails to secure Block-0 placement, our engine automatically aborts the trade rather than buying at a severely inflated price, protecting our capital from instant dumps."
    },
    {
      q: "Is priority fee tuning automatic?",
      a: "Yes. The bot dynamically calculates the necessary priority fees based on the current cluster TPS (transactions per second) and the historical fee averages of competing snipers in the last 100 blocks. This guarantees our transactions are highly competitive without wasting unnecessary SOL."
    },
    {
      q: "What exactly is a rug pull?",
      a: "A rug pull is when a token's creator drains the value out of it and disappears. The classic form is liquidity removal: the dev pulls the SOL out of the trading pool, leaving holders with tokens that can't be sold for anything. Softer variants include the dev dumping a huge hidden supply on buyers, or a honeypot contract that lets you buy but blocks you from selling. On Pump.fun, roughly 98-99% of launched tokens die or rug — which is exactly why automated filtering matters more than conviction."
    },
    {
      q: "How does the bonding curve reduce rug risk?",
      a: "On Pump.fun, the liquidity lives inside the bonding-curve smart contract itself — the dev never holds it, so the classic 'pull the LP' rug is structurally impossible before graduation. When the token migrates to Raydium, the liquidity is deposited by the protocol and the LP tokens are burned automatically. This design is a big part of why sniping the migration moment is safer than buying random fresh launches."
    },
    {
      q: "What red flags does the bot screen for before sniping?",
      a: "The engine scores every candidate on: holder concentration (top-10 wallets owning more than ~25% of supply is a veto), dev wallet behavior (prior rugs on linked wallets, pre-funded sniper clusters), bundled buys at launch (one entity posing as many buyers), social footprint age, and the mint/freeze authorities being revoked. A token failing any hard filter is skipped no matter how fast its curve is filling."
    },
    {
      q: "What is a honeypot and can the bot detect one?",
      a: "A honeypot is a token contract designed so buyers cannot sell — transfer functions are restricted to whitelisted wallets, or sell taxes are set to 100%. Before committing capital the engine simulates a full buy-and-sell round trip against the live pool state; if the simulated exit fails or returns drastically less than expected, the token is blacklisted instantly. Standard SPL tokens with revoked authorities can't hide these tricks, which is another reason migration candidates are screened for authority status."
    },
    {
      q: "Why do most memecoins die even without a rug?",
      a: "Attention is the only fundamental a memecoin has. A launch competes with thousands of others the same day for the same pool of degen liquidity; if it doesn't reach escape velocity within hours, holders rotate to the next ticker and the chart bleeds out. No exploit needed — just entropy. This is why the strategy holds positions for minutes, not weeks: it monetizes the attention spike around migration and exits before gravity resumes."
    },
    {
      q: "What are bundled snipers and why are they a threat?",
      a: "Sophisticated dev teams launch a token and simultaneously buy it from dozens of fresh wallets in the same block — a 'bundle'. The chart shows explosive organic-looking demand, but one entity controls most of the float and will dump it as one. The engine fingerprints these clusters by funding source: wallets bought in block 0 that were all funded from a common parent minutes earlier get treated as a single whale, and the token's holder-distribution score collapses accordingly."
    },
    {
      q: "What does 'LP burned' mean and why should I care?",
      a: "When liquidity is added to an AMM pool, the provider receives LP tokens — a claim check that can withdraw the pool's SOL at any time. If the dev keeps them, they hold a loaded gun pointed at every holder. Burning the LP tokens (sending them to an unspendable address) makes the liquidity permanent. Raydium pools created by Pump.fun graduation have their LP burned automatically, which is a key safety property this module relies on."
    },
    {
      q: "How does the bot manage exits on such volatile tokens?",
      a: "Entries are only half the trade. The engine uses a laddered exit: it takes a first profit tranche quickly to de-risk the principal, trails the remainder behind a volume-weighted stop, and hard-exits everything on rug signatures — a single wallet dumping over a set percentage of supply, liquidity migrating out, or buy volume collapsing below the 5-minute baseline. On memecoins, being decisively early out matters more than being maximally right."
    }
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 font-sans selection:bg-teal-500/30 pb-20">
      <div className="max-w-[1500px] mx-auto px-4 md:px-8 py-12">
        <div className="mb-12 border-b border-slate-800 pb-8">
          <span className="text-teal-400 font-bold text-xs uppercase tracking-wider">// Module 02</span>
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight mt-3 text-slate-100">
            Pump Sniper
          </h1>
          <p className="text-slate-300 mt-5 max-w-3xl text-base leading-relaxed">
            This bot interfaces directly with WebSockets to monitor real-time token creation and trades. When a token successfully completes its bonding curve, it migrates to Raydium. This module identifies these graduating tokens at block 0 to capture early liquidity advantages.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-8 shadow-sm">
            <Database size={28} className="text-teal-400 mb-5" />
            <h3 className="text-xl font-bold mb-3 text-slate-100">Bonding Curve Analysis</h3>
            <p className="text-slate-300 text-sm leading-relaxed">
              Tokens must hit a deterministic market cap to trigger migration. The bot tracks wallet accumulation patterns and curve percentages to predict migration exactly 2 blocks before graduation.
            </p>
          </div>
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-8 shadow-sm">
            <Lightning size={28} className="text-indigo-400 mb-5" />
            <h3 className="text-xl font-bold mb-3 text-slate-100">AMM Raydium Execution</h3>
            <p className="text-slate-300 text-sm leading-relaxed">
              Once graduation occurs, liquidity is seeded into the AMM. The bot utilizes Jito or direct RPC nodes to submit prioritized transactions, securing the earliest possible position in the new pool.
            </p>
          </div>
        </div>

        <MigrationRadar radarTokens={radarTokens} />
        <ExecutionLog trades={trades} />

        {/* EDUCATION SECTION */}
        <div className="mt-24 max-w-[1100px] mx-auto">
          <div className="text-center mb-12">
            <span className="text-teal-400 font-bold text-xs uppercase tracking-wider bg-teal-500/10 px-3 py-1.5 rounded-full border border-teal-500/20">// Deep Dive</span>
            <h2 className="text-4xl font-extrabold text-slate-100 mt-5 mb-3">Memecoins, Rug Pulls & Surviving the Trenches</h2>
            <p className="text-slate-300 max-w-2xl mx-auto leading-relaxed">
              The most dangerous corner of crypto — and why structure beats luck in it.
            </p>
          </div>

          <div className="space-y-6">
            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-8 shadow-sm">
              <h3 className="text-xl font-bold text-slate-100 mb-3">The Memecoin Casino, Explained</h3>
              <p className="text-slate-300 leading-relaxed text-sm sm:text-base">
                A memecoin has no product, no revenue, and no roadmap — its only asset is attention. Thousands launch on Solana every single day, each one a lottery ticket on going viral. The overwhelming majority go to zero within hours; a handful 100x. Pump.fun industrialized this by making token creation cost a few dollars and take thirty seconds, funneling every launch through the same standardized bonding curve. That standardization is the sniper's edge: when every casino table has identical rules, a machine can play them all simultaneously and only sit down when the odds tilt.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-slate-900 border border-slate-800 rounded-2xl p-8 shadow-sm">
                <h3 className="text-lg font-bold text-slate-100 mb-3">Anatomy of a Rug Pull</h3>
                <p className="text-slate-300 leading-relaxed text-sm">
                  Rugs come in three main flavors. <span className="text-slate-100 font-semibold">Liquidity rugs</span>: the dev withdraws the pool's SOL, instantly zeroing the token. <span className="text-slate-100 font-semibold">Supply rugs</span>: the team quietly holds a massive share of tokens across many wallets and dumps into the hype. <span className="text-slate-100 font-semibold">Honeypots</span>: the contract allows buying but blocks selling, trapping every entrant. Each leaves an on-chain fingerprint before it happens — concentrated holders, unburned LP, unrevoked authorities — and each fingerprint is machine-readable.
                </p>
              </div>
              <div className="bg-slate-900 border border-slate-800 rounded-2xl p-8 shadow-sm">
                <h3 className="text-lg font-bold text-slate-100 mb-3">Why the Bonding Curve Changed the Game</h3>
                <p className="text-slate-300 leading-relaxed text-sm">
                  Before Pump.fun, every launch required the dev to create and control the liquidity pool — the rug was one click away by design. The bonding curve inverted that: liquidity accumulates inside a neutral smart contract the dev cannot touch, prices follow a deterministic formula, and at ~$69k market cap the protocol itself migrates everything to Raydium and burns the LP tokens. Pre-graduation liquidity rugs became structurally impossible. The scams didn't disappear — they moved to supply concentration and coordinated dumping, which is exactly what modern screening targets.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-slate-900 border border-slate-800 rounded-2xl p-8 shadow-sm">
                <h3 className="text-lg font-bold text-slate-100 mb-3">The Graduation Moment</h3>
                <p className="text-slate-300 leading-relaxed text-sm">
                  Migration to Raydium is the single most information-dense moment in a memecoin's life. It proves real demand (~85 SOL of genuine buying filled the curve), it triggers listings on DexScreener and Birdeye where thousands of traders watch for new pools, and it seeds deep, burned liquidity for the first time. The result is a predictable retail volume surge in the minutes after graduation — the exact window this module trades. Everything before migration is filtering; everything after is execution speed.
                </p>
              </div>
              <div className="bg-slate-900 border border-slate-800 rounded-2xl p-8 shadow-sm">
                <h3 className="text-lg font-bold text-slate-100 mb-3">Survival Rules of the Trenches</h3>
                <p className="text-slate-300 leading-relaxed text-sm">
                  The discipline the engine enforces is the same a careful human should: never chase a chart that already ran, treat every token as a rug until the on-chain checks prove otherwise, size positions so a 100% loss is an annoyance rather than a disaster, take profit mechanically instead of emotionally, and never mistake a green candle for due diligence. Machines follow these rules on every trade at 3 a.m.; humans follow them until the first big win convinces them they don't need to.
                </p>
              </div>
            </div>

            <div className="bg-teal-500/5 border border-teal-500/20 rounded-2xl p-8 shadow-sm">
              <h3 className="text-xl font-bold text-slate-100 mb-3">Reading the On-Chain Tells</h3>
              <p className="text-slate-300 leading-relaxed text-sm sm:text-base">
                Every scam telegraphs itself on-chain if you know where to look. Healthy launch: hundreds of unique holders, top-10 wallets under a quarter of supply, dev wallet holding little, mint and freeze authorities revoked, steady two-sided volume. Pre-rug profile: a dozen wallets funded from one parent minutes before launch, buy-side volume from the same cluster, dormant whale wallets that received tokens for free, and social accounts created last week. The Migration Radar above distills these signals into the quality filters each candidate must pass — holders, liquidity, and market cap thresholds — before the bot will ever commit a lamport.
              </p>
            </div>
          </div>
        </div>

        {/* FAQ SECTION */}
        <div className="mt-20 max-w-[900px] mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-extrabold text-slate-100 mb-2">Sniper Knowledge Base</h2>
            <p className="text-slate-300">Technical insights into the migration sniper.</p>
          </div>
          <div className="space-y-2">
            {faqs.map((faq, i) => (
              <FaqItem key={i} q={faq.q} a={faq.a} />
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}
